import { FiChevronDown, SlRefresh } from '@/components/react-icons/main';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import { cleanClass } from '@shared/utils/cleanClass.js';
import { randomId } from '@shared/utils/randomId.js';
import { useRef, useEffect } from 'react';

const Select = ({
  borderless = false,
  fitWidth = false,
  showIcon = true,
  showOptionIcon = false,
  showReload = true,
  size = 'md',
  label,
  description,
  placeholder = 'Select an option...',
  className = '',
  style = {},
  customValue = false,
  compact = false,

  disabled = false,
  readOnly = true,
  nullable = false,
  useSearch = true,
  multiple = false,
  operator = false,

  value,
  options,
  onChange,
  htmlSize,
  floatingSelectV2Props = {},
  loading,
  dataArgumentKey,
  reload,
  reloadTitle,
  actionIcon,
  actionTitle,
  onAction,
  autoFocus = false
}) => {
  const triggerClass = randomId();
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || /^[a-zA-Z]$/.test(e.key)) && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
      const optionWrapper = document.querySelector('.option-wrapper');
      if (!optionWrapper) {
        e.preventDefault();
        inputRef.current?.click();

        if (/^[a-zA-Z]$/.test(e.key)) {
          setTimeout(() => {
            document.querySelector('.option-wrapper input').value = e.key;
          }, 10);
        }
      }
    }
  };

  const handleIconClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    onChange(e);
    inputRef.current?.focus();
  };

  const handleReload = () => {
    if (disabled || typeof reload !== 'function') {
      return;
    }
    reload();
  };

  const handleAction = () => {
    if (disabled || typeof onAction !== 'function') {
      return;
    }
    onAction();
  };

  const nullableLabel = floatingSelectV2Props?.nullableLabel || 'None';
  const actualValue = value?.value !== undefined ? value.value : value;
  const isValueNull = actualValue === null;
  const shouldShowNone = (nullable || floatingSelectV2Props?.nullable) && isValueNull;

  const labelValue = shouldShowNone ? nullableLabel : value?.label || value?.value || value || '';
  const length = labelValue.length + 1;
  const inputStyle = fitWidth ? { width: `${length < 10 ? length : 13}ch` } : {};
  const inputValue = customValue || (multiple ? `${labelValue.length > 0 ? `${labelValue.length} selected` : ''}` : labelValue);

  const selectedOption = options?.find((opt) => opt?.value === inputValue || opt?.label === inputValue);
  const optionIcon = showOptionIcon && selectedOption?.icon;

  return (
    <div className={cleanClass(`comp-select ${className} ${description ? 'has-description' : ''} ${compact ? 'compact' : ''}`)} style={style} data-argument-key={dataArgumentKey}>
      {label && <div className={'label'}>{label}</div>}
      {description && <div className={'description'}>{description}</div>}
      <div className={cleanClass(`wrapper ${triggerClass} ${readOnly ? 'pointer' : ''} ${disabled ? 'disabled' : ''} ${borderless ? 'borderless' : ''} ${size ? `size-${size}` : ''}`)}>
        {optionIcon && <IconV2 icon={optionIcon} className="option-icon" />}
        {compact ? (
          <div data-custom-value={!!customValue} ref={inputRef} className={cleanClass(`value`)}>
            {inputValue || placeholder}
          </div>
        ) : (
          <input data-custom-value={!!customValue} ref={inputRef} readOnly={readOnly} placeholder={placeholder} className={cleanClass(`input non-selectable`)} value={inputValue} style={inputStyle} onChange={onChange} onKeyDown={handleKeyDown} size={htmlSize} />
        )}
        {actionIcon && onAction && <IconV2 icon={actionIcon} className="select-action-icon" onMouseUp={handleAction} title={actionTitle} />}
        {showReload && (reload || loading) && <IconV2 icon={SlRefresh} className={`reload-icon ${loading ? 'loading' : ''}`} onMouseUp={handleReload} title={reloadTitle || 'Reload Data'} />}
        {showIcon && <IconV2 icon={FiChevronDown} className={`select-icon light ${disabled ? 'disabled' : ''}`} onClick={handleIconClick} />}
      </div>
      {!disabled && <FloatingSelectV2 triggers={[`.${triggerClass}`]} value={shouldShowNone ? null : actualValue} options={options} onChange={handleChange} matchTriggerWidth multiple={multiple} fullDisplay={operator ? { layout: [6, 2, 2, 2, 2] } : false} {...floatingSelectV2Props} useSearch={useSearch} nullable={nullable} loading={loading} />}
    </div>
  );
};

export default Select;
